import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { api, ApiError } from '../api/client';
import './AdminPanels.css';

/* ═══════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════ */

interface Panel {
  id: string;
  reseller_id: string | null;
  email: string;
  capacity: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface BulkInput {
  resellerId: string;
  email: string;
  password: string;
  totpSecret: string;
  capacity: number;
}

interface EditFields {
  resellerId: string;
  email: string;
  password: string;
  totpSecret: string;
  capacity: number;
  notes: string;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

const PAGE_SIZE = 10;
const PAGE_INCREMENT = 10;

const PASTE_PLACEHOLDER = `Paste tab-separated rows from Google Sheets, one per line:

ID	EMAIL	PASSWORD	2FA	CAPACITY

For example:
S3962	admin@srknlabs4884.shop	Qsj^u/~)%ZDr...	hrom 2sn2 3io2 ...	19`;

/* ═══════════════════════════════════════════════════════════════
   Inline SVG icons (no external deps)
   ═══════════════════════════════════════════════════════════════ */

const svg = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const SearchIcon = (): JSX.Element => (
  <svg className="ap-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="7" cy="7" r="5" /><line x1="11" y1="11" x2="14.5" y2="14.5" />
  </svg>
);
const EditIcon = (): JSX.Element => (
  <svg width="16" height="16" {...svg}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = (): JSX.Element => (
  <svg width="16" height="16" {...svg}>
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const XIcon = (): JSX.Element => (
  <svg width="16" height="16" {...svg}><path d="M18 6L6 18M6 6l12 12" /></svg>
);

/* ═══════════════════════════════════════════════════════════════
   Bulk-paste parser (client-side)
   ═══════════════════════════════════════════════════════════════ */

/**
 * Pre-process pasted text: handles a Google Sheets paste artifact where
 * long 2FA cells cause the trailing CAPACITY value to wrap onto its own
 * line. We detect "orphan" lines (just whitespace + a number) and stitch
 * them onto the previous under-tabbed row as the missing 5th column.
 */
function preprocessLines(text: string): string[] {
  const raw = text.split('\n').map((l) => l.replace(/\r$/, ''));
  const out: string[] = [];
  let i = 0;
  while (i < raw.length) {
    const line = raw[i] ?? '';
    if (line.trim().length === 0) {
      i += 1;
      continue;
    }
    const tabCount = (line.match(/\t/g) ?? []).length;
    // Need 4 tabs for 5 columns. If under-tabbed and next non-empty line is
    // just a number (the capacity that wrapped), merge them.
    if (tabCount < 4 && i + 1 < raw.length) {
      const next = (raw[i + 1] ?? '').trim();
      if (/^\d+$/.test(next)) {
        out.push(`${line}\t${next}`);
        i += 2;
        continue;
      }
    }
    out.push(line);
    i += 1;
  }
  return out;
}

function parseRows(text: string): BulkInput[] {
  const lines = preprocessLines(text);
  if (lines.length === 0) {
    throw new Error('Nothing to add — paste at least one row.');
  }
  return lines.map((line, i) => {
    const cols = line.split('\t');
    if (cols.length !== 5) {
      throw new Error(
        `Row ${i + 1}: expected 5 tab-separated columns (ID, EMAIL, PASSWORD, 2FA, CAPACITY), got ${cols.length}.`,
      );
    }
    const [resellerIdRaw, emailRaw, passwordRaw, totpRaw, capacityRaw] = cols;
    const resellerId = (resellerIdRaw ?? '').trim();
    const email = (emailRaw ?? '').trim();
    const password = (passwordRaw ?? '').trim();
    const totpSecret = (totpRaw ?? '').trim();
    const capacityStr = (capacityRaw ?? '').trim();
    if (!resellerId) throw new Error(`Row ${i + 1}: ID is empty.`);
    if (!email.includes('@')) throw new Error(`Row ${i + 1}: invalid email "${email}".`);
    if (!password) throw new Error(`Row ${i + 1}: password is empty.`);
    if (!totpSecret) throw new Error(`Row ${i + 1}: 2FA secret is empty.`);
    const capacity = Number.parseInt(capacityStr, 10);
    if (Number.isNaN(capacity) || capacity < 0) {
      throw new Error(`Row ${i + 1}: invalid capacity "${capacityStr}".`);
    }
    return { resellerId, email, password, totpSecret, capacity };
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ═══════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════ */

export function AdminPanels(): JSX.Element {
  // Data
  const [panels, setPanels] = useState<Panel[]>([]);
  const [loading, setLoading] = useState(true);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Lazy window
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Slide-over edit
  const [detail, setDetail] = useState<Panel | null>(null);
  const [edit, setEdit] = useState<EditFields | null>(null);
  const [saving, setSaving] = useState(false);

  // Single delete
  const [deleteTarget, setDeleteTarget] = useState<Panel | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Add panels (paste form)
  const [pasteText, setPasteText] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [pasteFeedback, setPasteFeedback] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Toast
  const [toast, setToast] = useState<Toast | null>(null);
  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  /* ─── data fetch ─────────────────────────────────────────── */

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const list = await api<Panel[]>('/api/panels');
      setPanels(list);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load panels');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /* ─── keyboard shortcuts ─────────────────────────────────── */

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        if (detail) setDetail(null);
        else if (deleteTarget) setDeleteTarget(null);
        else if (bulkDeleteOpen) setBulkDeleteOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [detail, deleteTarget, bulkDeleteOpen]);

  /* ─── filtering ──────────────────────────────────────────── */

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return panels;
    const term = searchTerm.toLowerCase().trim();
    return panels.filter(
      (p) =>
        p.email.toLowerCase().includes(term) ||
        (p.reseller_id?.toLowerCase().includes(term) ?? false),
    );
  }, [panels, searchTerm]);

  // Reset visible window when filter changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  /* ─── infinite scroll sentinel ───────────────────────────── */

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_INCREMENT, filtered.length));
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, filtered.length, visibleCount]);

  /* ─── selection ──────────────────────────────────────────── */

  const toggleSelect = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((p) => selected.has(p.id));

  const toggleSelectAll = (): void => {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const p of filtered) next.delete(p.id);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const p of filtered) next.add(p.id);
        return next;
      });
    }
  };

  /* ─── slide-over edit ────────────────────────────────────── */

  const openDetail = (panel: Panel): void => {
    setDetail(panel);
    setEdit({
      resellerId: panel.reseller_id ?? '',
      email: panel.email,
      password: '',
      totpSecret: '',
      capacity: panel.capacity,
      notes: panel.notes ?? '',
    });
  };

  const closeDetail = (): void => {
    setDetail(null);
    setEdit(null);
  };

  const saveEdit = async (): Promise<void> => {
    if (!detail || !edit) return;
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};
      const trimmedReseller = edit.resellerId.trim();
      const originalReseller = detail.reseller_id ?? '';
      if (trimmedReseller !== originalReseller) {
        patch.resellerId = trimmedReseller || null;
      }
      if (edit.email.trim() !== detail.email) patch.email = edit.email.trim();
      if (edit.password) patch.password = edit.password;
      if (edit.totpSecret) patch.totpSecret = edit.totpSecret;
      if (edit.capacity !== detail.capacity) patch.capacity = edit.capacity;
      const trimmedNotes = edit.notes.trim();
      const originalNotes = detail.notes ?? '';
      if (trimmedNotes !== originalNotes) {
        patch.notes = trimmedNotes || null;
      }

      if (Object.keys(patch).length === 0) {
        showToast('success', 'No changes to save');
        closeDetail();
        return;
      }

      await api<Panel>(`/api/panels/${detail.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      showToast('success', 'Panel updated');
      closeDetail();
      await refresh();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  /* ─── delete (single) ────────────────────────────────────── */

  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api(`/api/panels/${deleteTarget.id}`, { method: 'DELETE' });
      const id = deleteTarget.id;
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (detail?.id === id) closeDetail();
      setDeleteTarget(null);
      showToast('success', 'Panel deleted');
      await refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  /* ─── delete (bulk) ──────────────────────────────────────── */

  const confirmBulkDelete = async (): Promise<void> => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const result = await api<{ deleted: number }>('/api/panels/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      setBulkDeleteOpen(false);
      setSelected(new Set());
      showToast('success', `Deleted ${result.deleted} ${result.deleted === 1 ? 'panel' : 'panels'}`);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  /* ─── add panels (paste) ─────────────────────────────────── */

  const handleAdd = async (): Promise<void> => {
    setPasteError(null);
    setPasteFeedback(null);
    let rows: BulkInput[];
    try {
      rows = parseRows(pasteText);
    } catch (err) {
      setPasteError(err instanceof Error ? err.message : 'Parse error');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api<{ inserted: number; updated: number }>('/api/panels/bulk', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      });
      const parts: string[] = [];
      if (result.inserted > 0) parts.push(`${result.inserted} added`);
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      setPasteFeedback(parts.join(', ') || 'No changes');
      setPasteText('');
      await refresh();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Submission failed';
      setPasteError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── derived stats ──────────────────────────────────────── */

  const total = panels.length;
  const totalCapacity = panels.reduce((sum, p) => sum + p.capacity, 0);

  /* ─── render ─────────────────────────────────────────────── */

  return (
    <div className="ap-root">
      <div className="ap-scroll">
        <header className="ap-header">
          <div className="ap-header-row">
            <div>
              <h1 className="ap-title">Panels w/ Auto-License Off</h1>
              <p className="ap-subtitle">
                Pool of panels that have had auto-licensing disabled — fed into the License Assignment workflow. Paste rows from Google Sheets to add or update.
              </p>
            </div>
            <button
              type="button"
              className={`ap-btn ${pasteOpen ? 'ap-btn--secondary' : 'ap-btn--primary'}`}
              onClick={() => setPasteOpen((o) => !o)}
            >
              {pasteOpen ? 'Hide add form' : '+ Add panels'}
            </button>
          </div>
        </header>

        <section className="ap-stats">
          <div className="ap-stat">
            <div className="ap-stat-icon ap-stat-icon--coral">
              <svg width="22" height="22" {...svg}>
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M3 5v14a9 3 0 0 0 18 0V5" />
                <path d="M3 12a9 3 0 0 0 18 0" />
              </svg>
            </div>
            <div className="ap-stat-value">{total}</div>
            <div className="ap-stat-label">Total panels</div>
          </div>
          <div className="ap-stat">
            <div className="ap-stat-icon ap-stat-icon--sage">
              <svg width="22" height="22" {...svg}>
                <path d="M3 3v18h18" />
                <path d="M7 14l4-4 4 4 5-5" />
              </svg>
            </div>
            <div className="ap-stat-value">{totalCapacity}</div>
            <div className="ap-stat-label">Total capacity</div>
          </div>
        </section>

        {pasteOpen && (
          <section className="ap-card ap-add-card">
            <div className="ap-card-header">
              <h2 className="ap-card-title">Add Panels</h2>
              <p className="ap-card-hint">
                Tab-separated columns: <span className="ap-mono">ID</span>{' '}
                <span className="ap-mono">EMAIL</span> <span className="ap-mono">PASSWORD</span>{' '}
                <span className="ap-mono">2FA</span> <span className="ap-mono">CAPACITY</span>.
                Pasting an existing email overwrites that row.
              </p>
            </div>
            <div className="ap-card-body">
              <textarea
                className="ap-textarea"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={PASTE_PLACEHOLDER}
                spellCheck={false}
                rows={8}
                disabled={submitting}
              />
              {pasteError && <div className="ap-error">{pasteError}</div>}
              {pasteFeedback && <div className="ap-feedback">{pasteFeedback}</div>}
            </div>
            <div className="ap-card-footer">
              <button
                type="button"
                className="ap-btn ap-btn--ghost"
                onClick={() => {
                  setPasteText('');
                  setPasteError(null);
                  setPasteFeedback(null);
                }}
                disabled={submitting || pasteText.length === 0}
              >
                Clear
              </button>
              <button
                type="button"
                className="ap-btn ap-btn--primary"
                onClick={handleAdd}
                disabled={submitting || pasteText.trim().length === 0}
              >
                {submitting ? 'Adding…' : 'Add panels'}
              </button>
            </div>
          </section>
        )}

        <section className="ap-card ap-list-card">
          <div className="ap-list-toolbar">
            <div className="ap-search">
              <SearchIcon />
              <input
                ref={searchRef}
                type="text"
                className="ap-search-input"
                placeholder="Search by ID or email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
              {searchTerm.length === 0 && (
                <span className="ap-search-kbd">
                  <kbd className="ap-kbd">⌘</kbd>
                  <kbd className="ap-kbd">K</kbd>
                </span>
              )}
              {searchTerm.length > 0 && (
                <button
                  type="button"
                  className="ap-search-clear"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <XIcon />
                </button>
              )}
            </div>
            <div className="ap-toolbar-summary">
              {searchTerm ? (
                <span>
                  {filtered.length} of {panels.length} panels
                </span>
              ) : (
                <span>{panels.length} panels</span>
              )}
            </div>
          </div>

          {!loading && panels.length === 0 && (
            <div className="ap-empty">
              <div className="ap-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                  <path d="M3 5v14a9 3 0 0 0 18 0V5" />
                </svg>
              </div>
              <h3 className="ap-empty-title">No panels yet</h3>
              <p className="ap-empty-text">
                Click <strong>+ Add panels</strong> above to paste your first batch.
              </p>
            </div>
          )}

          {!loading && panels.length > 0 && filtered.length === 0 && (
            <div className="ap-empty">
              <div className="ap-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <h3 className="ap-empty-title">No panels match "{searchTerm}"</h3>
              <p className="ap-empty-text">Try a different search term, or clear the search.</p>
            </div>
          )}

          {filtered.length > 0 && (
            <>
              <table className="ap-table">
                <thead>
                  <tr>
                    <th className="ap-col-check">
                      <input
                        type="checkbox"
                        className="ap-checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAll}
                        title={allFilteredSelected ? 'Deselect all' : 'Select all visible'}
                      />
                    </th>
                    <th className="ap-col-id">ID</th>
                    <th className="ap-col-email">Email</th>
                    <th className="ap-col-num">Capacity</th>
                    <th className="ap-col-date">Added</th>
                    <th className="ap-col-actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => {
                    const isSelected = selected.has(p.id);
                    const isActive = detail?.id === p.id;
                    return (
                      <tr
                        key={p.id}
                        className={`ap-row ${isSelected ? 'ap-row--selected' : ''} ${isActive ? 'ap-row--active' : ''}`}
                        onClick={() => openDetail(p)}
                      >
                        <td className="ap-col-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="ap-checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(p.id)}
                          />
                        </td>
                        <td className="ap-cell-mono">{p.reseller_id ?? '—'}</td>
                        <td className="ap-cell-mono">{p.email}</td>
                        <td className="ap-col-num">{p.capacity}</td>
                        <td className="ap-col-date ap-cell-mono ap-cell-muted">
                          {formatDate(p.created_at)}
                        </td>
                        <td className="ap-col-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="ap-row-actions">
                            <button
                              type="button"
                              className="ap-row-action"
                              title="Edit panel"
                              onClick={() => openDetail(p)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              type="button"
                              className="ap-row-action ap-row-action--danger"
                              title="Delete panel"
                              onClick={() => setDeleteTarget(p)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hasMore && (
                <div ref={sentinelRef} className="ap-sentinel">
                  Showing {visibleCount} of {filtered.length} — scroll for more
                </div>
              )}
              {!hasMore && filtered.length > PAGE_SIZE && (
                <div className="ap-sentinel ap-sentinel--end">
                  All {filtered.length} panels shown
                </div>
              )}
            </>
          )}

          {loading && (
            <div className="ap-empty">
              <div className="ap-empty-text">Loading…</div>
            </div>
          )}
        </section>
      </div>

      {/* ─── Slide-over edit panel ─────────────────────────── */}

      <div
        className={`ap-overlay ${detail ? 'ap-open' : ''}`}
        onClick={closeDetail}
        aria-hidden={!detail}
      />
      <div className={`ap-slideover ${detail ? 'ap-open' : ''}`} role="dialog" aria-hidden={!detail}>
        {detail && edit && (
          <>
            <div className="ap-slideover-header">
              <span className="ap-slideover-title">Panel Details</span>
              <button type="button" className="ap-icon-btn" onClick={closeDetail} title="Close">
                <XIcon />
              </button>
            </div>
            <div className="ap-slideover-body">
              <div className="ap-identity">
                <div className="ap-identity-icon">
                  {(detail.reseller_id ?? detail.email.slice(0, 2)).slice(0, 4).toUpperCase()}
                </div>
                <div className="ap-identity-id">{detail.reseller_id ?? '—'}</div>
                <div className="ap-identity-email">{detail.email}</div>
                <div className="ap-identity-meta">
                  {detail.capacity} licenses · added {formatDate(detail.created_at)}
                </div>
              </div>

              <div className="ap-section">
                <div className="ap-section-title">Identifiers</div>
                <div className="ap-field">
                  <label className="ap-label">ID (reseller)</label>
                  <input
                    className="ap-input"
                    value={edit.resellerId}
                    onChange={(e) => setEdit({ ...edit, resellerId: e.target.value })}
                    placeholder="S3962"
                  />
                </div>
                <div className="ap-field">
                  <label className="ap-label">Email</label>
                  <input
                    className="ap-input"
                    type="email"
                    value={edit.email}
                    onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="ap-section">
                <div className="ap-section-title">Credentials</div>
                <div className="ap-field">
                  <label className="ap-label">Password</label>
                  <input
                    className="ap-input ap-input-mono"
                    type="text"
                    value={edit.password}
                    onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                    placeholder="Leave blank to keep current"
                    autoComplete="off"
                  />
                  <p className="ap-hint">Stored encrypted. Leaving this blank keeps the existing value.</p>
                </div>
                <div className="ap-field">
                  <label className="ap-label">2FA secret</label>
                  <input
                    className="ap-input ap-input-mono"
                    type="text"
                    value={edit.totpSecret}
                    onChange={(e) => setEdit({ ...edit, totpSecret: e.target.value })}
                    placeholder="Leave blank to keep current"
                    autoComplete="off"
                  />
                  <p className="ap-hint">Google Authenticator format (e.g. <span className="ap-mono">hrom 2sn2 …</span>).</p>
                </div>
              </div>

              <div className="ap-section">
                <div className="ap-section-title">Settings</div>
                <div className="ap-field">
                  <label className="ap-label">Capacity</label>
                  <input
                    className="ap-input"
                    type="number"
                    min="0"
                    value={edit.capacity}
                    onChange={(e) =>
                      setEdit({ ...edit, capacity: Number.parseInt(e.target.value || '0', 10) })
                    }
                  />
                </div>
                <div className="ap-field">
                  <label className="ap-label">Notes</label>
                  <textarea
                    className="ap-input"
                    value={edit.notes}
                    onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                    rows={3}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="ap-section ap-danger">
                <div className="ap-danger-title">Danger zone</div>
                <p className="ap-danger-text">
                  Deleting this panel removes it permanently, including its encrypted credentials.
                </p>
                <button
                  type="button"
                  className="ap-btn ap-btn--danger"
                  onClick={() => setDeleteTarget(detail)}
                >
                  Delete panel
                </button>
              </div>
            </div>

            <div className="ap-slideover-footer">
              <button type="button" className="ap-btn ap-btn--ghost" onClick={closeDetail}>
                Cancel
              </button>
              <button
                type="button"
                className="ap-btn ap-btn--primary"
                onClick={saveEdit}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ─── Floating selection bar ────────────────────────── */}

      <div className={`ap-selbar ${selected.size > 0 ? 'ap-visible' : ''}`}>
        <span className="ap-selbar-count">{selected.size} selected</span>
        <div className="ap-selbar-actions">
          <button
            type="button"
            className="ap-selbar-btn ap-selbar-btn--delete"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <TrashIcon />
            Delete
          </button>
          <button
            type="button"
            className="ap-selbar-btn ap-selbar-btn--deselect"
            onClick={() => setSelected(new Set())}
          >
            Deselect
          </button>
        </div>
      </div>

      {/* ─── Single delete modal ───────────────────────────── */}

      {deleteTarget && (
        <div className="ap-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3 className="ap-modal-title">Delete this panel?</h3>
              <button
                type="button"
                className="ap-icon-btn"
                onClick={() => setDeleteTarget(null)}
                title="Close"
              >
                <XIcon />
              </button>
            </div>
            <div className="ap-modal-body">
              <p>This will permanently delete the panel and its encrypted credentials. This cannot be undone.</p>
              <div className="ap-modal-target">
                <span className="ap-mono">{deleteTarget.reseller_id ?? '—'}</span>
                <span className="ap-mono">{deleteTarget.email}</span>
              </div>
            </div>
            <div className="ap-modal-footer">
              <button
                type="button"
                className="ap-btn ap-btn--ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ap-btn ap-btn--danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete panel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Bulk delete modal ─────────────────────────────── */}

      {bulkDeleteOpen && (
        <div className="ap-modal-overlay" onClick={() => setBulkDeleteOpen(false)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3 className="ap-modal-title">
                Delete {selected.size} {selected.size === 1 ? 'panel' : 'panels'}?
              </h3>
              <button
                type="button"
                className="ap-icon-btn"
                onClick={() => setBulkDeleteOpen(false)}
                title="Close"
              >
                <XIcon />
              </button>
            </div>
            <div className="ap-modal-body">
              <p>The following panels will be permanently deleted:</p>
              <ul className="ap-modal-list">
                {Array.from(selected)
                  .slice(0, 6)
                  .map((id) => {
                    const p = panels.find((x) => x.id === id);
                    if (!p) return null;
                    return (
                      <li key={id}>
                        <span className="ap-mono">{p.reseller_id ?? '—'}</span>
                        <span className="ap-mono">{p.email}</span>
                      </li>
                    );
                  })}
                {selected.size > 6 && (
                  <li className="ap-modal-list-more">… and {selected.size - 6} more</li>
                )}
              </ul>
            </div>
            <div className="ap-modal-footer">
              <button
                type="button"
                className="ap-btn ap-btn--ghost"
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ap-btn ap-btn--danger"
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting
                  ? 'Deleting…'
                  : `Delete ${selected.size} ${selected.size === 1 ? 'panel' : 'panels'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Toast ─────────────────────────────────────────── */}

      {toast && <div className={`ap-toast ap-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}
